import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { Alert } from "@mui/material";
import { Person } from "@mui/icons-material";
import { useProfiles } from "../../../../hooks";
import { Keypad } from "./Keypad";

interface TouchKeyboardLoginProps {
  open: boolean;
  onLoginSuccess: (operatorCode: string) => void;
  onClose: () => void;
}

export const TouchKeyboardLogin = ({
  open,
  onLoginSuccess,
  onClose,
}: TouchKeyboardLoginProps) => {
  const [inputCode, setInputCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { isOperator, isSupervisor } = useProfiles();

  // Clear input when modal opens
  useEffect(() => {
    if (open) {
      setInputCode("");
      setError(null);
    }
  }, [open]);

  const handleNumberInput = useCallback((digit: string) => {
    setInputCode((prev) => {
      if (prev.length < 4) {
        // Limit to 4 digits max
        setError(null);
        return prev + digit;
      }
      return prev;
    });
  }, []);

  const handleBackspace = useCallback(() => {
    setInputCode((prev) => prev.slice(0, -1));
    setError(null);
  }, []);

  const handleClear = useCallback(() => {
    setInputCode("");
    setError(null);
  }, []);

  const handleLogin = useCallback(async () => {
    if (!inputCode) {
      setError("Please enter your operator code");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Simulate network delay like old system
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Validate operator codes based on user profile
      const validOperatorCodes = ["1234", "4321"];
      const validSupervisorCodes = ["999"];

      let isValidLogin = false;
      let errorMessage = "";

      if (validOperatorCodes.includes(inputCode)) {
        // User entered operator code - check if they have BYI Operator profile
        if (isOperator) {
          isValidLogin = true;
        } else {
          errorMessage =
            "You don't have operator permissions. Please contact your administrator.";
        }
      } else if (validSupervisorCodes.includes(inputCode)) {
        // User entered supervisor code - check if they have BYI Supervisor profile
        if (isSupervisor) {
          isValidLogin = true;
        } else {
          errorMessage =
            "You don't have supervisor permissions. Please contact your administrator.";
        }
      } else {
        errorMessage = "Invalid operator code. Please try again.";
      }

      if (isValidLogin) {
        onLoginSuccess(inputCode);
      } else {
        setError(errorMessage);
      }
    } catch (err) {
      setError("Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [inputCode, isOperator, isSupervisor, onLoginSuccess]);

  // Add keyboard event handling
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // Prevent default for keys we handle
      if (event.key >= "0" && event.key <= "9") {
        event.preventDefault();
        handleNumberInput(event.key);
      } else if (event.key === "Enter") {
        event.preventDefault();
        if (inputCode && !isLoading) {
          handleLogin();
        }
      } else if (event.key === "Backspace") {
        event.preventDefault();
        handleBackspace();
      } else if (event.key === "Delete") {
        event.preventDefault();
        handleClear();
      } else if (event.key === "Escape") {
        event.preventDefault();
        if (!isLoading) {
          onClose();
        }
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    open,
    inputCode,
    isLoading,
    handleLogin,
    handleNumberInput,
    handleBackspace,
    handleClear,
    onClose,
  ]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: (theme) =>
            theme.palette.billetYard.backgrounds.light,
          padding: "40px",
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          mb={2}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            mb={1}
          >
            <Person
              sx={{
                fontSize: "32px",
                color: (theme) => theme.palette.primary.main,
                marginRight: "12px",
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: (theme) => theme.palette.primary.main,
                textAlign: "center",
              }}
            >
              Enter Operator Code
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              textAlign: "center",
              fontSize: "14px",
            }}
          >
            Use touch buttons below or keyboard (Enter to login • Esc to cancel)
          </Typography>
        </Box>

        {/* Input Display */}
        <Box
          sx={{
            minHeight: "80px",
            minWidth: "300px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: (theme) => theme.palette.background.paper,
            border: (theme) => `2px solid ${theme.palette.primary.main}`,
            borderRadius: "8px",
            marginBottom: "24px",
            fontSize: "32px",
            fontWeight: "bold",
            color: (theme) => theme.palette.primary.main,
            letterSpacing: "8px",
            fontFamily: "monospace",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          {inputCode || "____"}
        </Box>

        {/* Error Display */}
        {error && (
          <Box mb={3} sx={{ width: "100%", maxWidth: "500px" }}>
            <Alert severity="error" sx={{ fontSize: "18px" }}>
              {error}
            </Alert>
          </Box>
        )}

        {/* Keypad */}
        <Box sx={{ marginBottom: "32px", width: "100%", maxWidth: "500px" }}>
          <Keypad
            onKeypadInput={handleNumberInput}
            onClear={handleClear}
            onBackspace={handleBackspace}
            disabled={isLoading}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          padding: "24px",
          gap: "16px",
          justifyContent: "center",
        }}
      >
        {/* Cancel Button */}
        <Button
          onClick={onClose}
          variant="outlined"
          size="large"
          disabled={isLoading}
          sx={{
            minWidth: "150px",
            minHeight: "60px",
            fontSize: "18px",
            fontWeight: "bold",
            borderColor: (theme) => theme.palette.grey[500],
            color: (theme) => theme.palette.grey[500],
            borderRadius: "8px",
          }}
        >
          Cancel
        </Button>

        {/* Login Button */}
        <Button
          onClick={handleLogin}
          variant="contained"
          size="large"
          disabled={!inputCode || isLoading}
          sx={{
            minWidth: "200px",
            minHeight: "60px",
            fontSize: "18px",
            fontWeight: "bold",
            backgroundColor: (theme) =>
              isLoading ? theme.palette.grey[400] : theme.palette.primary.main,
            color: "white",
            borderRadius: "8px",
          }}
          startIcon={
            isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : undefined
          }
        >
          {isLoading ? "LOGGING IN..." : "LOGIN"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
